import React, { useState, useEffect } from 'react';
import { apiRequest } from '../../utils/apiUtils';
import { useUI } from '../../contexts/UIContext';
import QueryStatsCards from '../../components/admin/QueryStatsCards';
import { BoltIcon, ExclamationTriangleIcon } from '@heroicons/react/24/outline';

/**
 * Query Performance Page
 *
 * Admin page for monitoring query performance metrics.
 * Shows slow queries, failed queries, and performance statistics.
 */
const QueryPerformancePage = () => {
  const { showNotification } = useUI();

  const [performanceData, setPerformanceData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dateRange, setDateRange] = useState(7); // days
  const [selectedConnection, setSelectedConnection] = useState('');
  const [slowThreshold, setSlowThreshold] = useState(5000); // ms
  const [connections, setConnections] = useState([]);

  useEffect(() => {
    loadConnections();
  }, []);

  useEffect(() => {
    loadPerformanceData();
  }, [dateRange, selectedConnection, slowThreshold]);

  const loadConnections = async () => {
    try {
      const response = await apiRequest('/connections', { skipThrottle: true });
      if (response?.data) {
        setConnections(response.data);
      }
    } catch (error) {
      console.error('Error loading connections:', error);
    }
  };

  const loadPerformanceData = async () => {
    try {
      setLoading(true);

      const params = {
        slow_threshold_ms: slowThreshold
      };

      // Calculate date range
      const endDate = new Date();
      const startDate = new Date();
      startDate.setDate(startDate.getDate() - dateRange);

      params.start_date = startDate.toISOString();
      params.end_date = endDate.toISOString();

      if (selectedConnection) {
        params.connection_id = selectedConnection;
      }

      const response = await apiRequest('/admin/query-logs/performance', {
        params,
        skipThrottle: true
      });

      if (response?.data) {
        setPerformanceData(response.data);
      } else {
        setPerformanceData(null);
      }
    } catch (error) {
      console.error('Error loading performance data:', error);
      showNotification('Failed to load performance data', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleDateRangeChange = (days) => {
    setDateRange(days);
  };

  const formatDuration = (ms) => {
    if (ms == null) return 'N/A';
    if (ms < 1000) return `${ms}ms`;
    return `${(ms / 1000).toFixed(2)}s`;
  };

  const formatDate = (isoDate) => {
    if (!isoDate) return 'N/A';
    return new Date(isoDate).toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const truncateText = (text, maxLength = 100) => {
    if (!text) return 'N/A';
    if (text.length <= maxLength) return text;
    return text.substring(0, maxLength) + '...';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        <span className="ml-2 text-gray-600">Loading performance data...</span>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h2 className="text-2xl font-bold text-gray-900">Query Performance</h2>
        <p className="text-sm text-gray-600 mt-1">
          Monitor query execution times, identify slow queries, and track failures.
        </p>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg border border-gray-200 p-4">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {/* Date Range Filter */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Date Range
            </label>
            <div className="flex items-center gap-1 bg-gray-100 rounded-lg p-1">
              {[7, 30, 90].map((days) => (
                <button
                  key={days}
                  onClick={() => handleDateRangeChange(days)}
                  className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${
                    dateRange === days
                      ? 'bg-white text-blue-600 shadow-sm'
                      : 'text-gray-600 hover:bg-gray-50'
                  }`}
                >
                  {days} Days
                </button>
              ))}
            </div>
          </div>

          {/* Connection Filter */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Connection
            </label>
            <select
              value={selectedConnection}
              onChange={(e) => setSelectedConnection(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">All Connections</option>
              {connections.map((conn) => (
                <option key={conn.id} value={conn.id}>
                  {conn.name}
                </option>
              ))}
            </select>
          </div>

          {/* Slow Query Threshold */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Slow Query Threshold
            </label>
            <select
              value={slowThreshold}
              onChange={(e) => setSlowThreshold(Number(e.target.value))}
              className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value={1000}>1 second</option>
              <option value={3000}>3 seconds</option>
              <option value={5000}>5 seconds</option>
              <option value={10000}>10 seconds</option>
              <option value={30000}>30 seconds</option>
            </select>
          </div>
        </div>
      </div>

      {/* Performance Metrics Cards */}
      {performanceData && (
        <QueryStatsCards
          stats={performanceData.performance_metrics}
          loading={false}
          variant="performance"
        />
      )}

      {/* Slow Queries Section */}
      {performanceData && performanceData.slow_queries && (
        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
              <BoltIcon className="h-5 w-5 text-yellow-500" />
              Slow Queries ({performanceData.slow_query_count})
            </h3>
            <span className="text-sm text-gray-500">
              Slower than {formatDuration(performanceData.slow_threshold_ms)}
            </span>
          </div>

          {performanceData.slow_queries.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-300">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                      Executed At
                    </th>
                    <th className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                      Connection
                    </th>
                    <th className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                      Query
                    </th>
                    <th className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                      Type
                    </th>
                    <th className="px-3 py-3.5 text-right text-sm font-semibold text-gray-900">
                      Duration
                    </th>
                    <th className="px-3 py-3.5 text-right text-sm font-semibold text-gray-900">
                      Rows Scanned
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 bg-white">
                  {performanceData.slow_queries.map((query) => (
                    <tr key={query.id} className="hover:bg-gray-50">
                      <td className="px-3 py-4 text-sm text-gray-500 whitespace-nowrap">
                        {formatDate(query.executed_at)}
                      </td>
                      <td className="px-3 py-4 text-sm text-gray-900">
                        {query.connection_name || 'Unknown'}
                      </td>
                      <td className="px-3 py-4 text-sm text-gray-700 font-mono max-w-md">
                        {truncateText(query.query_text, 80)}
                      </td>
                      <td className="px-3 py-4 text-sm text-gray-600 whitespace-nowrap">
                        {query.query_type || 'N/A'}
                      </td>
                      <td className="px-3 py-4 text-sm text-orange-600 text-right font-medium">
                        {formatDuration(query.execution_time_ms)}
                      </td>
                      <td className="px-3 py-4 text-sm text-gray-600 text-right">
                        {query.rows_scanned != null
                          ? query.rows_scanned.toLocaleString()
                          : 'N/A'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="text-center py-8 bg-gray-50 rounded-lg">
              <BoltIcon className="h-10 w-10 text-gray-400 mx-auto mb-2" />
              <p className="text-sm text-gray-600">
                No slow queries found. All queries are performing well!
              </p>
            </div>
          )}
        </div>
      )}

      {/* Failed Queries Section */}
      {performanceData && performanceData.failed_queries && (
        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
              <ExclamationTriangleIcon className="h-5 w-5 text-red-500" />
              Failed Queries ({performanceData.failed_query_count})
            </h3>
          </div>

          {performanceData.failed_queries.length > 0 ? (
            <div className="space-y-4">
              {performanceData.failed_queries.map((query) => (
                <div
                  key={query.id}
                  className="border border-red-200 rounded-lg p-4 bg-red-50"
                >
                  <div className="flex items-start justify-between mb-2">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="text-sm font-medium text-gray-900">
                          {query.connection_name || 'Unknown Connection'}
                        </span>
                        <span className="text-xs text-gray-500">
                          {formatDate(query.executed_at)}
                        </span>
                      </div>
                      <p className="text-sm font-mono text-gray-700">
                        {truncateText(query.query_text, 150)}
                      </p>
                    </div>
                  </div>
                  <div className="bg-white border border-red-200 rounded p-3 mt-3">
                    <p className="text-xs font-semibold text-red-900 mb-1">
                      Error Code: {query.error_code || 'Unknown'}
                    </p>
                    <p className="text-sm text-red-800">{query.error_message || 'No error message available'}</p>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8 bg-gray-50 rounded-lg">
              <ExclamationTriangleIcon className="h-10 w-10 text-gray-400 mx-auto mb-2" />
              <p className="text-sm text-gray-600">
                No failed queries. All queries executed successfully!
              </p>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default QueryPerformancePage;
